import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { StatsContainer, MetricCard } from 'components/v2/stats';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import {
  getCarrierScoreMetric,
  getCarrierOnTimeMetric,
  getCarrierStopTimelinessMetric,
  getCarrierPODTimelinessMetric,
  getCarrierPODQualityMetric,
  getCarrierCompletionRateMetric,
  getCarrierAppUsageMetric,
} from 'utils/MetricAPIUtils';
import { handleDataStream, convertJsonToCSV } from 'utils/StreamHandlingUtils';
import SessionStore from 'stores/SessionStore';
import Badge from 'components/v2/Badge';
import Container from 'components/v2/container';
import Spinner from 'components/ui/Spinner';
import FetchWebAPIUtils from 'utils/FetchWebAPIUtils';
import DriverWebAPIUtils from 'utils/DriverWebAPIUtils';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import CalendarIcon from '../../v2/icons/CalendarIcon';
import DriverMetricsList from '../../v2/driver-metric-list';
import CarrierPerformanceGraph from './Graph.js';

const weeks = {
  ONE: 1,
  TWO: 2,
  SIX: 6,
};

const timeFilters = [
  {
    name: 'This week',
    value: weeks.ONE,
    localizedSlug: 'THIS_WEEK',
  },
  {
    name: '2 weeks ago',
    value: weeks.TWO,
    localizedSlug: '2_WEEKS_AGO',
  },
  {
    name: '6 weeks ago',
    value: weeks.SIX,
    localizedSlug: '6_WEEKS_AGO',
  },
];

const Dashboard = ({ id }) => {
  const { t } = useTranslation();
  const [timeAgo, setTimeAgo] = useState(weeks.SIX);
  const [stats, setStats] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [isExporting, setExporting] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [carrierName, setCarrierName] = useState('');
  let abortController;

  useEffect(() => {
    setStats(getStatConfigs(id, timeAgo, getTimeFilters()));
    const getDrivers = async () => {
      const carrier = await UserWebAPIUtils.getV2(id);
      setCarrierName(carrier.name ? carrier.name : 'Unknown');
      const response = await DriverWebAPIUtils.getDriverNamesByCarrier(id);
      setDrivers(response);
    };
    getDrivers();
  }, [id, timeAgo]);

  function handleSelectTime(e) {
    setTimeAgo(parseInt(e.target.value, 10));
  }

  const getTimeFilters = () => {
    switch (timeAgo) {
      case 2:
        return {
          startDate: moment().subtract(2, 'week').startOf('day').toISOString(),
          endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
          deltaStart: moment().subtract(4, 'week').startOf('day').toISOString(),
          deltaEnd: moment()
            .subtract(2, 'day')
            .subtract(2, 'week')
            .endOf('day')
            .toISOString(),
        };
      case 6:
        return {
          startDate: moment().subtract(6, 'week').startOf('day').toISOString(),
          endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
          deltaStart: moment()
            .subtract(12, 'week')
            .startOf('day')
            .toISOString(),
          deltaEnd: moment()
            .subtract(2, 'day')
            .subtract(6, 'week')
            .endOf('day')
            .toISOString(),
        };
      default:
        return {
          startDate: moment().subtract(1, 'week').startOf('day').toISOString(),
          endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
          deltaStart: moment().subtract(2, 'week').startOf('day').toISOString(),
          deltaEnd: moment()
            .subtract(2, 'day')
            .subtract(1, 'week')
            .endOf('day')
            .toISOString(),
        };
    }
  };

  const exportCSV = async () => {
    const payload = {
      date: timeAgo,
      type: 'carrier',
      account: id,
    };
    if (abortController) {
      await abortController();
    }
    setExporting(true);
    const totalRowCountResponse =
      await FetchWebAPIUtils.getMetricScorecardCSVExportRowCount(payload);
    setTotalRowCount(totalRowCountResponse.total);
    const { response, controller } =
      await FetchWebAPIUtils.getMetricScorecardCSVExport(payload);
    abortController = controller;
    handleDataStream(response, handleStreamEnd);
  };

  const handleStreamEnd = (rows) => {
    setExporting(false);
    const filename =
      drivers && drivers.length
        ? `${drivers[0].carrier.name}_scorecard.csv`
        : 'carrier_scorecard.csv';
    return convertJsonToCSV(rows, filename);
  };

  return (
    <Container className="py-8">
      {drivers && drivers.length > 0 && (
        <div className="flex flex-wrap justify-between">
          <div className="flex">
            <div className="flex">
              <h2 className="font-medium my-0 mb-4">
                {drivers[0].carrier.name}
              </h2>
              <span className="p-2">
                <Badge color="blue" size="large" rounded>
                  {titleize(t('CARRIER', { count: 1 }))}
                </Badge>
              </span>
            </div>
          </div>
          <div>
            {SessionStore.isMetricsAdminUser() && (
              <span className="flex">
                {isExporting && (
                  <p className="pr-2 my-auto">
                    {t('RETRIEVING_TOTAL_ROW', { totalRowCount })}
                  </p>
                )}
                <button
                  className="button button-green"
                  onClick={exportCSV}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <Spinner message={`${t('EXPORTING')}...`} />
                  ) : (
                    t('EXPORT')
                  )}
                </button>
              </span>
            )}
          </div>
        </div>
      )}
      <h2 className="mb-4">{carrierName}</h2>
      <h3 className="my-0 mb-4">{t('PERFORMANCE')}</h3>
      <div className="flex items-center w-64 mb-4">
        <span className="flex items-center justify-center px-3 py-2 text-gray-700 border border-r-0 border-gray-200 rounded-l shadow-sm bg-gray100">
          <CalendarIcon />
        </span>
        <select
          className="w-full rounded-none rounded-r form-select"
          onChange={handleSelectTime}
          value={timeAgo}
        >
          {timeFilters.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {t(filter.localizedSlug)}
            </option>
          ))}
        </select>
      </div>

      <StatsContainer>
        {stats.map((item) => {
          const {
            getMetric,
            label,
            type,
            id: itemId,
            info,
            maxRating,
            params,
          } = item;

          return (
            <MetricCard
              getMetric={getMetric}
              label={t(label)}
              type={type}
              id={itemId}
              key={itemId}
              info={t(info)}
              maxRating={maxRating}
              params={params}
            />
          );
        })}
      </StatsContainer>
      <div className="mt-8">
        <CarrierPerformanceGraph
          id={id}
          timeAgo={timeAgo}
          setDashboardTime={setTimeAgo}
        />
      </div>
      <div className="mt-8">
        <h3 className="my-0 mb-4">{t('DRIVER_METRIC')}</h3>
        <DriverMetricsList drivers={drivers} dateFilters={getTimeFilters()} />
      </div>
    </Container>
  );
};

Dashboard.propTypes = {
  id: PropTypes.string.isRequired,
};

function getStatConfigs(id, timeAgo, dateFilters) {
  return [
    {
      getMetric: async () => {
        const params = {
          ...dateFilters,
          delta: true,
        };
        const { stars: value } = await getCarrierScoreMetric(id, params);
        if (value) {
          return { value: (value / 5) * 100 };
        }
        return { value: 0 };
      },
      label: 'OVERALL',
      type: 'percent',
      info: 'OVERALL_INFO',
      id: 'overall',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const params = {
          ...dateFilters,
          delta: true,
        };
        const { score: value, deltaScore } = await getCarrierOnTimeMetric(
          id,
          params,
        );
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'ON_TIME',
      type: 'percent',
      info: 'ON_TIME_INFO',
      id: 'on_time',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const params = {
          ...dateFilters,
          weights: {},
        };
        const { score: value, deltaScore } =
          await getCarrierStopTimelinessMetric(id, params);
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'STOP_COMPLETION_TIMELINESS',
      type: 'percent',
      info: 'STOP_COMPLETION_TIMELINESS_INFO',
      id: 'stop_completion_timeliness',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const { score: value, deltaScore } =
          await getCarrierPODTimelinessMetric(id, dateFilters);
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'POD_TIMELINESS',
      type: 'percent',
      info: 'POD_TIMELINESS_INFO',
      id: 'pod_timelines',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const { score: value, deltaScore } = await getCarrierPODQualityMetric(
          id,
          dateFilters,
        );
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'POD_QUALITY',
      type: 'percent',
      info: 'POD_QUALITY_INFO',
      id: 'pod_quality',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const { score: value, deltaScore } =
          await getCarrierCompletionRateMetric(id, dateFilters);
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'COMPLETION_RATE',
      type: 'percent',
      info: 'COMPLETION_RATE_INFO',
      id: 'completion_rate',
      params: [id, timeAgo],
    },
    {
      getMetric: async () => {
        const { score: value, deltaScore } = await getCarrierAppUsageMetric(
          id,
          dateFilters,
        );
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        if (deltaScore) {
          data.delta = deltaScore;
        }
        return data;
      },
      label: 'APP_USAGE',
      type: 'percent',
      info: 'APP_USAGE_INFO',
      id: 'app_usage',
      params: [id, timeAgo],
    },
  ];
}

export default Dashboard;
